import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserInvitations, useDeleteInvitation, useUpdateInvitation } from '@/hooks/useInvitations';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  ArrowLeft,
  MoreVertical,
  Edit,
  Users,
  ExternalLink,
  Copy,
  Trash2,
  Eye,
  Globe,
  GlobeLock,
  Mail,
  Heart,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseCalendarDate } from '@/lib/parseCalendarDate';
import { toast } from 'sonner';

const Invitations = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: invitations = [], isLoading } = useUserInvitations();
  const deleteInvitation = useDeleteInvitation();
  const updateInvitation = useUpdateInvitation();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState<string | null>(null);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  const handleCopyLink = (slug: string) => {
    const link = `${window.location.origin}/i/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado al portapapeles');
  };

  const handleTogglePublish = (id: string, currentStatus: boolean, isPaid: boolean) => {
    if (!currentStatus && !isPaid) {
      toast.error('Necesitas pagar para publicar. Completa el pago desde el panel.');
      return;
    }
    updateInvitation.mutate({
      id,
      data: { is_published: !currentStatus },
    });
  };

  const handleDelete = () => {
    if (selectedInvitationId) {
      deleteInvitation.mutate(selectedInvitationId);
      setDeleteDialogOpen(false);
      setSelectedInvitationId(null);
    }
  };

  const openDeleteDialog = (id: string) => {
    setSelectedInvitationId(id);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="min-h-screen flex bg-background">
      <DashboardSidebar />
      
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-medium text-foreground">
                  Mis Invitaciones
                </h1>
                <p className="text-muted-foreground text-sm">
                  Gestiona todas tus invitaciones de boda
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/invitations/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Invitación
            </Button>
          </div>

          {/* Invitations Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : invitations.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <div className="w-20 h-20 mx-auto rounded-full bg-muted/50 flex items-center justify-center mb-6">
                  <Heart className="w-10 h-10 text-muted-foreground/50" />
                </div>
                <h2 className="text-xl font-medium mb-2">Aún no tienes invitaciones</h2>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Crea tu primera invitación digital y compártela con tus invitados
                </p>
                <Button onClick={() => navigate('/invitations/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear mi primera invitación
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {invitations.map((inv) => (
                <Card key={inv.id} className="group hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={inv.is_published ? 'default' : 'secondary'}>
                            {inv.is_published ? (
                              <><Globe className="w-3 h-3 mr-1" /> Publicada</>
                            ) : (
                              <><GlobeLock className="w-3 h-3 mr-1" /> Borrador</>
                            )}
                          </Badge>
                        </div>
                        <h3 className="font-serif text-xl font-medium text-foreground">
                          {inv.bride_name} & {inv.groom_name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {format(parseCalendarDate(inv.wedding_date) ?? new Date(inv.wedding_date), "d 'de' MMMM, yyyy", { locale: es })}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/invitations/${inv.id}/edit`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/invitations/${inv.id}/guests`)}>
                            <Users className="w-4 h-4 mr-2" />
                            Gestionar Invitados
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {inv.is_published && (
                            <>
                              <DropdownMenuItem onClick={() => window.open(`/i/${inv.unique_slug}`, '_blank')}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Ver invitación
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleCopyLink(inv.unique_slug)}>
                                <Copy className="w-4 h-4 mr-2" />
                                Copiar link
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleTogglePublish(inv.id, inv.is_published, inv.is_paid)}
                            disabled={!inv.is_published && !inv.is_paid}
                          >
                            {inv.is_published ? (
                              <>
                                <GlobeLock className="w-4 h-4 mr-2" />
                                Despublicar
                              </>
                            ) : !inv.is_paid ? (
                              <>
                                <GlobeLock className="w-4 h-4 mr-2" />
                                Publicar (requiere pago)
                              </>
                            ) : (
                              <>
                                <Globe className="w-4 h-4 mr-2" />
                                Publicar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openDeleteDialog(inv.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2 mt-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/invitations/${inv.id}/edit`)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/invitations/${inv.id}/guests`)}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Invitados
                      </Button>
                      {inv.is_published && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/i/${inv.unique_slug}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar invitación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los datos asociados 
              incluyendo la lista de invitados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Invitations;
